import React, { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";

const GREEN = "#5f8f10";

/* Scroll-reveal wrapper. Fades + rises into view once. Pass delay for cascades.
   Respects prefers-reduced-motion: renders in final state, no entrance animation. */
export const Reveal = ({ children, delay = 0, y = 24, className = "", ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={reduce ? { duration: 0 } : { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/* Animated pyramid: builds up one row at a time (bottom -> top), finishing
   with the green apex. Greyscale gradient rising to a single green peak. */
export const PyramidBuild = ({ className = "", gap = "#F7F6F2" }) => {
  const reduce = useReducedMotion();
  const N = 5, B = 64, TH = 56, PAD = 8;
  const width = N * B + PAD * 2;
  const height = N * TH + PAD * 2;
  const cx = width / 2;
  const upFill = ["#5f8f10", "#2c312a", "#4e534b", "#7b7f77", "#a7aaa3"];
  const downFill = ["#5f8f10", "#363b33", "#585d54", "#868a82", "#b0b3ac"];
  const rows = [];
  for (let r = 0; r < N; r++) {
    const y0 = PAD + r * TH;
    const y1 = PAD + (r + 1) * TH;
    const xLeft = cx - ((r + 1) * B) / 2;
    const tris = [];
    for (let j = 0; j <= r; j++) {
      const ax = xLeft + j * B + B / 2;
      tris.push({ pts: `${xLeft + j * B},${y1} ${xLeft + j * B + B},${y1} ${ax},${y0}`, fill: upFill[r] });
    }
    for (let j = 0; j < r; j++) {
      const tl = xLeft + j * B + B / 2;
      const tr = xLeft + (j + 1) * B + B / 2;
      const bx = xLeft + (j + 1) * B;
      tris.push({ pts: `${tl},${y0} ${tr},${y0} ${bx},${y1}`, fill: downFill[r] });
    }
    rows.push({ r, tris });
  }
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={`mx-auto block h-auto w-full max-w-[440px] ${className}`} aria-hidden>
      {rows.map(({ r, tris }) => (
        <motion.g
          key={r}
          initial={reduce ? false : { opacity: 0, y: 30 }}
          whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={reduce ? { duration: 0 } : { duration: 0.5, delay: (N - 1 - r) * 0.22, ease: [0.22, 1, 0.36, 1] }}
        >
          {tris.map((t, i) => (
            <polygon key={i} points={t.pts} fill={t.fill} stroke={gap} strokeWidth="3" strokeLinejoin="round" />
          ))}
        </motion.g>
      ))}
    </svg>
  );
};

/* deterministic PRNG so the layout is stable across renders */
const mulberry32 = (a) => () => {
  a |= 0; a = (a + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const ArrowGlyph = ({ color }) => (
  <svg width="14" height="26" viewBox="0 0 14 26" fill="none" aria-hidden>
    <path d="M7 0 L13 9 L9 9 L9 26 L5 26 L5 9 L1 9 Z" fill={color} />
  </svg>
);

/* Scattered effort (off) resolving into a rising green trajectory (on). */
export const ArrowField = ({ active, className = "" }) => {
  const arrows = useMemo(() => {
    const rng = mulberry32(20260815);
    const lanes = 5;
    const perLane = 6;
    const out = [];
    for (let lane = 0; lane < lanes; lane++) {
      const laneX = 12 + lane * 19; // 12,31,50,69,88 (%)
      const isCenter = lane === 2;
      for (let k = 0; k < perLane; k++) {
        const alignedY = 6 + k * 15 + (lane % 2 ? 5 : 0);
        out.push({
          id: `${lane}-${k}`,
          laneX,
          alignedY,
          isCenter,
          dx: Math.round((rng() - 0.5) * 150),
          dy: Math.round((rng() - 0.5) * 130),
          rot: Math.round((rng() - 0.5) * 160),
          dur: (3 + rng() * 2.6).toFixed(2),
          delay: (rng() * 3.2).toFixed(2),
          jdur: (4 + rng() * 3).toFixed(2),
        });
      }
    }
    return out;
  }, []);

  return (
    <div aria-hidden className={`pointer-events-none relative ${className}`}>
      {/* rising green trajectory streak, only when aligned */}
      <div
        className="absolute bottom-0 top-0 blur-[6px] transition-opacity duration-700"
        style={{ left: "50%", width: 70, marginLeft: -35, opacity: active ? 0.6 : 0, background: `radial-gradient(ellipse at 50% 60%, rgba(95,143,16,0.55), transparent 70%)` }}
      />
      <div
        className={`absolute bottom-0 top-0 transition-opacity duration-700 ${active ? "af-streak" : ""}`}
        style={{
          left: "50%",
          width: 4,
          marginLeft: -2,
          opacity: active ? 0.9 : 0,
          backgroundImage: `linear-gradient(0deg, transparent 0%, ${GREEN} 30%, #a7d84a 50%, ${GREEN} 70%, transparent 100%)`,
          backgroundSize: "100% 220%",
        }}
      />

      {arrows.map((a) => {
        const color = active ? (a.isCenter ? GREEN : "rgba(95,143,16,0.55)") : "rgba(255,255,255,0.22)";
        return (
          <div
            key={a.id}
            className="absolute transition-transform duration-700 ease-out"
            style={{
              left: `${a.laneX}%`,
              top: `${a.alignedY}%`,
              transform: active ? "translate(0,0) rotate(0deg)" : `translate(${a.dx}px, ${a.dy}px) rotate(${a.rot}deg)`,
            }}
          >
            <div className={active ? "af-rise" : "af-jitter"} style={{ "--afd": active ? `${a.dur}s` : `${a.jdur}s`, animationDelay: `${a.delay}s` }}>
              <ArrowGlyph color={color} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* Eight lines drawing inward to a single pulsing green node (Collective Expertise). */
export const ConvergeField = ({ className = "" }) => {
  const cx = 150, cy = 180;
  const pts = [
    [40, 40], [150, 26], [260, 40],
    [28, 180], [272, 180],
    [40, 320], [150, 334], [260, 320],
  ];
  return (
    <svg viewBox="0 0 300 360" className={`h-full w-full ${className}`} aria-hidden>
      {pts.map(([x, y], i) => (
        <line key={i} x1={x} y1={y} x2={cx} y2={cy} stroke="rgba(255,255,255,0.28)" strokeWidth="1.5" className="converge-line" style={{ animationDelay: `${i * 0.18}s` }} />
      ))}
      {pts.map(([x, y], i) => (
        <rect key={`n${i}`} x={x - 4} y={y - 4} width="8" height="8" fill="rgba(255,255,255,0.35)" />
      ))}
      <circle cx={cx} cy={cy} r="22" fill="#5f8f10" opacity="0.18" className="node-pulse" />
      <rect x={cx - 9} y={cy - 9} width="18" height="18" fill="#5f8f10" />
    </svg>
  );
};
/* Four ways growth gets stuck: three grey blocked pathways + one highlighted
   green route, lines draw in on a loop. */
export const StuckField = ({ className = "" }) => (
  <svg viewBox="0 0 300 420" className={`h-full w-full ${className}`} aria-hidden>
    <path d="M24 96 L96 96 L128 60 L214 60" fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="converge-line" style={{ animationDelay: "0s" }} />
    <g stroke="rgba(255,255,255,0.32)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <path d="M24 210 L150 210" className="converge-line" style={{ animationDelay: "0.4s" }} />
      <path d="M150 210 L214 174" className="converge-line" style={{ animationDelay: "0.7s" }} />
      <path d="M150 210 L214 210" className="converge-line" style={{ animationDelay: "0.7s" }} />
      <path d="M150 210 L214 246" className="converge-line" style={{ animationDelay: "0.7s" }} />
    </g>
    <path d="M24 330 L92 330 L120 300 L160 300 L160 356 L214 356" fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="converge-line" style={{ animationDelay: "1s" }} />
    <line x1="266" y1="380" x2="266" y2="46" stroke="#5f8f10" strokeWidth="4" className="converge-line" style={{ animationDelay: "0.2s" }} />
    <circle cx="266" cy="210" r="16" fill="none" stroke="#5f8f10" strokeWidth="3" className="node-pulse" />
    <rect x="259" y="34" width="14" height="14" fill="#5f8f10" transform="rotate(45 266 41)" />
  </svg>
);


export const WorkingState = ({ active }) => {
  const rows = useMemo(() => {
    const rng = mulberry32(7);
    return Array.from({ length: 7 }, (_, i) => ({
      i,
      y: 26 + i * 36,
      green: i === 3 || i === 2 || i === 4,
      offL: -30 - Math.round(rng() * 26),
      offR: 30 + Math.round(rng() * 26),
      jy: Math.round((rng() - 0.5) * 22),
      rot: (rng() - 0.5) * 10,
    }));
  }, []);

  const grey = "#6f746b";
  return (
    <svg viewBox="0 0 400 300" className="h-full w-full" aria-hidden>
      {rows.map((r) => {
        const c = r.green ? GREEN : grey;
        const off = !active;
        return (
          <g key={r.i}>
            {/* connector, appears only when aligned */}
            <rect className="ws-link" x="150" y={r.y + 4} width="100" height="10" fill={c}
              style={{ opacity: active ? 0.9 : 0, transform: active ? "scaleX(1)" : "scaleX(0)", transformOrigin: "200px center" }} />
            {/* left segment */}
            <g className="ws-seg" style={{ transform: off ? `translate(${r.offL}px, ${r.jy}px) rotate(${r.rot}deg)` : "translate(0,0)", transformOrigin: "center" }}>
              <rect x="70" y={r.y} width="90" height="18" fill={c} opacity={off ? 0.55 : 1} />
            </g>
            {/* right segment */}
            <g className="ws-seg" style={{ transform: off ? `translate(${r.offR}px, ${-r.jy}px) rotate(${-r.rot}deg)` : "translate(0,0)", transformOrigin: "center" }}>
              <rect x="240" y={r.y} width="90" height="18" fill={c} opacity={off ? 0.55 : 1} />
            </g>
          </g>
        );
      })}
    </svg>
  );
};
